import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Overlay from "@/components/Overlay";
import { ARTICLES } from "@/data/content";

export default function Inspiration() {
  const [active, setActive] = useState(null);

  return (
    <section id="inspiration" data-testid="inspiration-section" className="bg-ivory py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <Eyebrow>Travel Inspiration</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 max-w-xl text-4xl leading-[1.15] text-forest md:text-5xl">
                The Mirevaa <span className="italic text-gold">Journal.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-forest-mist">
              Stories, guides and slow observations from the places we design — written to start the conversation your
              next journey grows from.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.slug} y={40} delay={i * 0.1}>
              <article data-testid={`article-card-${a.slug}`} className="group flex h-full flex-col">
                <button onClick={() => setActive(a)} data-testid={`article-open-${a.slug}`} className="text-left">
                  <div className="relative overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      className="h-64 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 md:h-72"
                    />
                    <span className="absolute left-4 top-4 bg-forest/85 px-3 py-1.5 text-[9px] uppercase tracking-widest2 text-gold">
                      {a.category}
                    </span>
                  </div>
                  <p className="mt-5 text-[10px] uppercase tracking-widest2 text-forest-mist">{a.read}</p>
                  <h3 className="mt-3 font-display text-2xl leading-snug text-forest transition-colors duration-500 group-hover:text-gold md:text-[1.65rem]">
                    {a.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-forest-mist">{a.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest2 font-medium text-forest">
                    <span className="gold-link">Read the Story</span>
                    <ArrowRight className="h-3.5 w-3.5 text-gold transition-transform duration-500 group-hover:translate-x-1.5" />
                  </span>
                </button>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <Overlay open={!!active} onClose={() => setActive(null)} image={active?.image} imageAlt={active?.title} label={active?.category}>
        {active && (
          <article data-testid="article-overlay-content">
            <p className="text-[10px] uppercase tracking-widest2 text-gold">
              {active.category} · {active.read}
            </p>
            <h3 className="mt-4 font-display text-3xl leading-tight text-forest md:text-4xl">{active.title}</h3>
            <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-forest-mist">
              {active.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="mt-10 border-t border-gold/40 pt-6">
              <p className="font-display text-xl italic text-forest">Inspired? Let's design your version.</p>
              <a
                data-testid="article-overlay-cta"
                href="#plan-your-journey"
                onClick={(e) => {
                  e.preventDefault();
                  setActive(null);
                  setTimeout(() => document.getElementById("plan-your-journey")?.scrollIntoView({ behavior: "smooth" }), 150);
                }}
                className="mt-4 inline-block bg-forest px-8 py-3.5 text-[11px] uppercase tracking-widest2 font-medium text-ivory transition-colors duration-500 hover:bg-gold hover:text-forest"
              >
                Plan Your Journey
              </a>
            </div>
          </article>
        )}
      </Overlay>
    </section>
  );
}
